<template>
  <expansion-panel
    key="expansion-panel-campaign"
    :title="$t('offer-manager.create.step-campaign.title')"
    :readonly="panelReadonly"
    :hideDivider="panelHideDivider">
    <template v-slot:panel-content>
      <Banner
        :text="
          $t('offer-manager.create.step-campaign.warning-campaign-not-available', {campaignName: hondaCampaign.name})
        "
        type="warning"
        v-if="
          hondaCampaign &&
          vehicleModelSubname &&
          !isCampaignsLoading &&
          hondaCampaignsList &&
          hondaCampaignsList.some(obj => obj.name === hondaCampaign.name && obj.isNotAvailable)
        " />
      <Banner
        :text="$t('offer-manager.create.step-campaign.warning-empty-campaigns-list')"
        type="warning"
        v-else-if="
          vehicleModel &&
          vehicleModelSubname &&
          !isCampaignsLoading &&
          hondaCampaignsList &&
          hondaCampaignsList.length === 0
        " />
      <m-form-row :label="$t('offer-manager.create.step-campaign.lbl-honda-campaign')">
        <v-row>
          <v-col class="col-8">
            <m-select
              autofocus
              return-object
              data-cy="campaignSelector"
              itemText="name"
              itemValue="id"
              :placeholder="$t('offer-manager.create.step-campaign.lbl-honda-campaign-placeholder')"
              :disabled="!hondaCampaignsList || hondaCampaignsList.length === 0 || disabled"
              :loading="isCampaignsLoading"
              :items="hondaCampaignsList"
              v-model="hondaCampaign"
              clearable
              @input="value => updateOffer('setHondaCampaign', value)" />
          </v-col>
          <v-col class="col-4">
            <mapit-input
              uid="offerVehicleHondaCampaign"
              :placeholder="$t('offer-manager.create.step-campaign.lbl-amount')"
              :type="InputTypes.PRICE"
              :negative-value="hondaCampaignValue < 0"
              :value="hondaCampaignValue"
              disabled />
          </v-col>
        </v-row>
      </m-form-row>
      <m-form-row :label="$t('offer-manager.create.step-campaign.lbl-range-dates-campaign')">
        <v-row no-gutters>
          <v-col class="col-6">
            <mapit-input
              uid="hondaCampaignStartDate"
              data-cy="campaignStartDate"
              :placeholder="$t('offer-manager.create.step-campaign.lbl-start-date-campaign-placeholder')"
              :value="hondaCampaignStartDate"
              appendIcon="fak fa-light fa-m-calendar"
              disabled />
          </v-col>
          <v-col class="col-6 pl-2">
            <mapit-input
              uid="hondaCampaignEndDate"
              data-cy="campaignEndDate"
              :placeholder="$t('offer-manager.create.step-campaign.lbl-end-date-campaign-placeholder')"
              :value="hondaCampaignEndDate"
              appendIcon="fak fa-light fa-m-calendar"
              disabled />
          </v-col>
        </v-row>
      </m-form-row>
      <m-form-row :label="$t('offer-manager.create.step-campaign.lbl-dealer-campaign')">
        <v-row>
          <v-col class="col-4">
            <mapit-input
              uid="offerVehicleCampaign"
              :placeholder="$t('offer-manager.create.step-campaign.lbl-dealer-campaign-placeholder')"
              :type="InputTypes.TEXT_FIELD_WITH_SUGGESTIONS"
              :value="dealerCampaignName"
              :rules="customDealerCampaignNameRules"
              @changeValue="value => updateOffer('setDealerCampaignName', value.trim())"
              :returnObject="true"
              :maxlength="100"
              :disabled="!vehicleModel || !vehicleModelSubname || disabled" />
          </v-col>
          <v-col class="col-4">
            <mapit-input
              uid="offerVehicleDiscountPriceRatePercent"
              :value="vehicleDiscountPriceRatePercent"
              :placeholder="$t('offer-manager.create.step-motorbike-data.lbl-price-discount-placeholder')"
              :type="InputTypes.PERCENTAGE"
              @changeValue="value => updateOffer('setVehicleDiscountPriceRatePercent', value)"
              :rules="discountPercentagePriceRateCCAARules"
              :forceFloat="true"
              :disabled="!vehiclePriceRateCCAA || disabled" />
          </v-col>
          <v-col class="col-4">
            <mapit-input
              uid="offerVehicleDiscountPriceRate"
              :value="vehicleDiscountPriceRate"
              :placeholder="$t('offer-manager.create.step-motorbike-data.lbl-price-discount-placeholder')"
              :type="InputTypes.PRICE"
              @changeValue="value => updateOffer('setVehicleDiscountPriceRate', value)"
              :rules="[...discountPriceRateCCAARules, ...customDealerCampaignPriceRules]"
              :forceFloat="true"
              :disabled="!vehiclePriceRateCCAA || disabled"
              :negativeValue="true" />
          </v-col>
        </v-row>
      </m-form-row>
      <m-form-row :label="$t('offer-manager.create.step-motorbike-data.lbl-motorbike-price')" highlighted>
        <mapit-input
          uid="offerVehicleTotalPrice"
          :value="parseFloat(vehicleTotalPrice || 0).toFixed(2)"
          placeholder="0.00"
          :type="InputTypes.PRICE"
          :isReadonly="true"
          :disabled="disabled" />
      </m-form-row>
    </template>
  </expansion-panel>
</template>

<script lang="ts">
import {Status} from '@muvio/honda-offers-builder/model'
import {computed, defineComponent, Ref, ref, watch} from '@vue/composition-api'

import Banner from '@/components/atom/Banner.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import MapitInput from '@/components/general/MapitInput.vue'
import {UseCampaignProps, useCampaigns} from '@/composables/campaigns'
import {discountPercentagePriceRateCCAARules, discountPriceRateCCAARules} from '@/helpers/offer-manager-rules'
import {useI18n} from '@/i18n'
import {InputTypes} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'

export default defineComponent({
  name: 'Campaign',
  components: {Banner, ExpansionPanel, MapitInput},
  props: {
    hideDetails: {
      type: Boolean,
      default: false,
    },
    panelReadonly: {
      type: Boolean,
      default: false,
    },
    panelHideDivider: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, {root, emit}) {
    const {t} = useI18n()
    const error = ref('')

    const {
      getRegistrationPrice: registrationPrice,
      getRegistrationDiscount: registrationDiscount,
      getRegistrationDiscountPercent: registrationDiscountPercent,
      getRegistrationTotalPrice: registrationTotalPrice,
      getVehicleTotalPrice: vehicleTotalPrice,
      getVehicleDiscountPriceRate: vehicleDiscountPriceRate,
      getVehicleDiscountPriceRatePercent: vehicleDiscountPriceRatePercent,
      getVehiclePriceRateCCAA: vehiclePriceRateCCAA,
      getBranch: branch,
      getLocale: locale,
      getVehicleModelName: vehicleModelName,
      getVehicleModelSubname: vehicleModelSubname,
      getSelectedModel: vehicleModel,
      getHondaCampaignValue: hondaCampaignValue,
      getHondaCampaignStartDate: hondaCampaignStartDate,
      getHondaCampaignEndDate: hondaCampaignEndDate,
      getHondaCampaign: hondaCampaign,
      getDealerCampaignName: dealerCampaignName,
      getDealerCampaignValue: dealerCampaignValue,
      getStatus: offerStatus,
    } = mapGetters(root.$store.getters)

    // Honda Campaign
    const campaignFetchProps = ref() as Ref<UseCampaignProps>

    watch(vehicleModel, vehicleModel => {
      campaignFetchProps.value = {
        branch: branch.value,
        model: vehicleModel?.model?.name,
        ym: vehicleModel?.model?.ym,
        type: vehicleModel?.model?.type,
        color: vehicleModel?.color?.code,
      }
    })

    const {campaigns: hondaCampaignsList, loading: isCampaignsLoading} = useCampaigns(campaignFetchProps)

    // Add the honda campaign to the list if is not there
    watch(hondaCampaignsList, campaignsList => {
      if (
        vehicleModel.value &&
        hondaCampaign.value?.name &&
        campaignsList &&
        !campaignsList.some(obj => obj.name === hondaCampaign.value.name)
      ) {
        hondaCampaignsList.value?.push({...hondaCampaign.value, isNotAvailable: true})
      }
    })

    // Clean the list if the vehicle changes
    watch([vehicleModelName, vehicleModelSubname], () => {
      hondaCampaignsList.value = []
    })

    watch(registrationPrice, validateDiscounts)
    watch(registrationDiscount, validateDiscounts)
    watch(registrationDiscountPercent, validateDiscounts)

    function validateDiscount(value: any) {
      return (
        !value ||
        !registrationPrice.value ||
        (registrationPrice.value &&
          parseFloat(value) <= parseFloat(registrationPrice.value) &&
          parseFloat(value) >= 0) ||
        'Error'
      )
    }

    function updateOffer(action: string, value: any) {
      root.$store.dispatch(action, value)
      emit('validateForm')
    }

    function validateDiscounts() {
      if (validateDiscount(registrationDiscount.value) !== 'Error') {
        if (registrationDiscountPercent.value >= 0 && registrationDiscountPercent.value <= 100) {
          error.value = ''
        } else {
          error.value = root.$t('offer-manager.create.step-campaign.lbl-error-discount-percentage') as string
        }
      } else {
        error.value = root.$t('offer-manager.create.step-campaign.lbl-error-discount') as string
      }
    }

    const customDealerCampaignPriceRules = computed(() => {
      return [
        (value: string | undefined | any) => {
          if (!value) return true
          const fValue =
            typeof value === 'string' ? parseFloat(value.replace('.', '').replace('-', '').replace(',', '.')) : value
          return (
            (fValue >= 0 && fValue <= parseFloat(vehiclePriceRateCCAA.value)) ||
            t('error.discount-price-rate').toString()
          )
        },
      ]
    })

    const customDealerCampaignNameRules = computed(() => {
      return [
        (name: string | undefined) => {
          if ((!name || !name?.trim()) && dealerCampaignValue.value) return t('error.required').toString()
        },
      ]
    })

    return {
      t,
      InputTypes,
      error,
      registrationPrice,
      registrationDiscount,
      registrationDiscountPercent,
      registrationTotalPrice,
      validateDiscount,
      updateOffer,
      validateDiscounts,
      vehicleTotalPrice,
      vehicleDiscountPriceRate,
      vehicleDiscountPriceRatePercent,
      locale,
      hondaCampaignsList,
      isCampaignsLoading,
      hondaCampaign,
      discountPriceRateCCAARules,
      discountPercentagePriceRateCCAARules,
      customDealerCampaignPriceRules,
      customDealerCampaignNameRules,
      vehiclePriceRateCCAA,
      vehicleModel,
      vehicleModelName,
      vehicleModelSubname,
      hondaCampaignStartDate,
      hondaCampaignEndDate,
      dealerCampaignName,
      dealerCampaignValue,
      hondaCampaignValue,
      offerStatus,
      Status,
    }
  },
})
</script>
